import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import getTimezoneObjectByAbbreviation from 'common/src/modules/timezones/utils/getTimezoneObjectByAbbreviation';
import {CLStore} from 'common/src/redux/store';
import {DateTime} from 'luxon';
import analyticsApi from '../../../../analytics.api';
import {DimensionValue, MetricValue} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';
import {campaignAnalyticsPerformingInputSchema} from '../campaignAnalyticsAgent.schemas';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import persistInlineWidgetToCampaignDashboard from '../utils/persistInlineWidgetToCampaignDashboard';

function getCampaignWorstPerformingTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolError('Campaign data is unavailable, please try again later');
  }

  async function execute({
    input: {dimension, metrics: aiMetrics, startDate, endDate, count, filter, type},
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof campaignAnalyticsPerformingInputSchema>): Promise<ToolResult> {
    const {
      campaignPage: {
        insightsPage: {measuredPeriod, availableFilters, availableDimensions},
        campaignTree: {campaign},
      },
    } = store.getState();

    if (!campaign) {
      return toolError('Campaign not found');
    }

    const metrics = aiMetrics as MetricValue[];
    const timezoneObject = await getTimezoneObjectByAbbreviation(measuredPeriod.timezone);
    const dateRange = {
      startDate: DateTime.fromISO(startDate).startOf('day').toUTC(),
      endDate: DateTime.fromISO(endDate).endOf('day').toUTC(),
    };

    const dimensionKey = getRemoteDimensionKey<DimensionValue>(availableDimensions, dimension);

    if (!dimensionKey) {
      return toolError('This dimension is not supported');
    }

    const filterKey = getRemoteDimensionKey<DimensionValue>(availableFilters, filter?.dimension);

    const campaignIdFilter = {
      filterBy: DimensionValue.CampaignID,
      allValues: [String(campaign.id)],
    };

    const allFilters = [
      campaignIdFilter,
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    const analyticsData = await analyticsApi.getWorstPerformingData({
      dimensions: [dimensionKey],
      filters: allFilters,
      metrics,
      dateRange,
      selectedTimezone: timezoneObject,
      eventType: null,
      ...(count ? {count} : {}),
    });

    if (!analyticsData.items.length) {
      return handleNoAnalyticsData();
    }

    const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

    if (!isSkillsMode) {
      return toolResult(JSON.stringify(analyticsData.items));
    }

    return persistInlineWidgetToCampaignDashboard({
      toolUtils,
      callChain,
      campaign,
      title: 'Worst Performing',
      requestedMaxRows: count,
      params: {
        metrics,
        dimensions: [dimensionKey],
        startDate,
        endDate,
        type,
        analyticsData: analyticsData.items,
        breakdownByTime: false,
      },
    });
  }

  return tool({
    name: CampaignAnalyticsToolName.GetCampaignWorstPerforming,
    displayName: 'Loading Analytics Data',
    description:
      'Retrieves the campaign "worst-performing" dimension, according to metrics (KPIs). Example: "Worst-performing creatives by CTR"',
    execute,
    inputSchema: campaignAnalyticsPerformingInputSchema,
  });
}

export default getCampaignWorstPerformingTool;
